import React, { useState, useMemo } from 'react';
import { FiZap, FiCopy } from 'react-icons/fi';
import { getTeeColor } from './teeColor';
import CellPicker from './CellPicker';
import HoleEntryKeypad from '../HoleEntryKeypad';
import { analyseSI, autoFixSI as fixSI } from '../../utils/strokeIndex';

const NINES = [{ label: 'Front 9', start: 0 }, { label: 'Back 9', start: 9 }];

const sumPars = (pars, start, end) =>
  pars.slice(start, end).reduce((s, p) => s + (Number(p) || 0), 0);

// Yardage is OPTIONAL — a club that doesn't publish it still gives a usable
// course — so an empty lengths row is never a validation failure, just an
// empty row. The unit defaults to yards only once a figure is entered.
const DEFAULT_LENGTH_UNIT = 'yards';
const UNITS = [{ key: 'yards', label: 'yds' }, { key: 'metres', label: 'm' }];

const StepPars = ({ tees, activeTeeKey, setActiveTeeKey, updateTeeHole, updateTee, copyHolesFromTee, applyHolesToAll }) => {
  const [editingCell, setEditingCell] = useState(null); // { field, holeIdx }
  const [showKeypad, setShowKeypad] = useState(false);
  const [showCopyMenu, setShowCopyMenu] = useState(false);

  const tee = tees.find(t => t._key === activeTeeKey) || tees[0];
  const otherTees = tees.filter(t => t._key !== tee._key);

  const { dupHoles, missing } = useMemo(() => analyseSI(tee.hole_stroke_indices), [tee.hole_stroke_indices]);
  // A tee loaded from an older record has no lengths array at all.
  const lengths = tee.hole_lengths?.length === 18 ? tee.hole_lengths : Array(18).fill('');
  const anyLength = lengths.some(v => v !== '' && v !== null && v !== undefined);
  const unit = tee.length_unit || DEFAULT_LENGTH_UNIT;
  const unitLabel = UNITS.find(u => u.key === unit)?.label || 'yds';
  const lengthTotal = lengths.reduce((s, v) => s + (Number(v) || 0), 0);
  const parTotal = sumPars(tee.hole_pars, 0, 18);
  const parsComplete = tee.hole_pars.every(p => Number(p) >= 3);
  const siComplete = missing.length === 0 && dupHoles.size === 0;

  const openCell = (field, holeIdx) => setEditingCell({ field, holeIdx });

  const CELL_FIELD = { par: 'hole_pars', si: 'hole_stroke_indices', length: 'hole_lengths' };

  const handleCellSelect = (value) => {
    if (!editingCell) return;
    const field = CELL_FIELD[editingCell.field];
    updateTeeHole(tee._key, field, editingCell.holeIdx, value);
    // The first yardage entered settles the unit, so the row is never a
    // column of numbers with no unit attached to them.
    if (field === 'hole_lengths' && !tee.length_unit && updateTee) {
      updateTee(tee._key, 'length_unit', DEFAULT_LENGTH_UNIT);
    }
    setEditingCell(null);
  };
  const handleCellClear = () => {
    if (!editingCell) return;
    updateTeeHole(tee._key, CELL_FIELD[editingCell.field], editingCell.holeIdx, '');
    setEditingCell(null);
  };

  // One-tap resolve — the shared rule, applied a cell at a time because the
  // wizard's card lives in the parent's tee state.
  const autoFixSI = () => {
    fixSI(tee.hole_stroke_indices)
      .forEach((v, idx) => updateTeeHole(tee._key, 'hole_stroke_indices', idx, v));
  };

  return (
    <div className="cw-section">
      {/* Tee selector */}
      {tees.length > 1 && (
        <div className="cw-tee-tabs">
          {tees.map(t => (
            <button
              key={t._key}
              className={`cw-tee-tab${t._key === tee._key ? ' cw-tee-tab--active' : ''}`}
              onClick={() => { setActiveTeeKey(t._key); setShowCopyMenu(false); }}
            >
              <span className="cw-tee-dot cw-tee-dot--sm" style={{ background: getTeeColor(t.name) }} />
              <span className="cw-tee-tab-name">{t.name || 'Untitled'}</span>
            </button>
          ))}
        </div>
      )}

      {/* Card tools */}
      <div className="cw-pars-tools">
        <button className="cw-quickfill" onClick={() => setShowKeypad(true)}>
          <FiZap size={14} /> Quick fill
        </button>
        {/* The unit the card PRINTS. Switching it relabels the row — it never
            converts the figures, because the stored yardage is the printed
            one and converting back and forth loses a yard at a time. */}
        <div className="cw-unit-toggle" role="group" aria-label="Length unit">
          {UNITS.map(u => (
            <button
              key={u.key}
              className={`cw-unit-btn${unit === u.key ? ' cw-unit-btn--active' : ''}`}
              onClick={() => updateTee && updateTee(tee._key, 'length_unit', u.key)}
            >
              {u.label}
            </button>
          ))}
        </div>
        {otherTees.length > 0 && (
          <div className="cw-copy-wrap">
            <button className="cw-copy-btn" onClick={() => setShowCopyMenu(v => !v)}>
              <FiCopy size={14} /> Copy / apply
            </button>
            {showCopyMenu && (
              <div className="cw-copy-menu">
                {otherTees.map(t => (
                  <button key={t._key} onClick={() => { copyHolesFromTee(tee._key, t._key); setShowCopyMenu(false); }}>
                    Copy from {t.name || 'Untitled'}
                  </button>
                ))}
                <button className="cw-copy-apply" onClick={() => { applyHolesToAll(tee._key); setShowCopyMenu(false); }}>
                  Apply this card to all tees
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Grid */}
      {NINES.map(({ label, start }) => (
        <div key={label} className="cw-nine">
          <div className="cw-nine-label">{label}</div>
          <div className="cw-grid-scroll">
            <div className="cw-grid">
              <div className="cw-grid-rowlabel">Hole</div>
              {Array.from({ length: 9 }, (_, i) => (
                <div key={i} className="cw-cell cw-cell--hole">{start + i + 1}</div>
              ))}

              <div className="cw-grid-rowlabel">Par</div>
              {Array.from({ length: 9 }, (_, i) => {
                const idx = start + i;
                const val = tee.hole_pars[idx];
                return (
                  <button
                    key={i}
                    className={`cw-cell cw-cell--par${!val ? ' cw-cell--empty' : ''}`}
                    onClick={() => openCell('par', idx)}
                  >
                    {val || '–'}
                  </button>
                );
              })}

              <div className="cw-grid-rowlabel">SI</div>
              {Array.from({ length: 9 }, (_, i) => {
                const idx = start + i;
                const val = tee.hole_stroke_indices[idx];
                const isDup = dupHoles.has(idx);
                return (
                  <button
                    key={i}
                    className={`cw-cell cw-cell--si${!val ? ' cw-cell--empty' : ''}${isDup ? ' cw-cell--dup' : ''}`}
                    onClick={() => openCell('si', idx)}
                  >
                    {val || '–'}
                  </button>
                );
              })}

              <div className="cw-grid-rowlabel">{unitLabel}</div>
              {Array.from({ length: 9 }, (_, i) => {
                const idx = start + i;
                const val = lengths[idx];
                return (
                  <button
                    key={i}
                    className={`cw-cell cw-cell--len${!val ? ' cw-cell--empty' : ''}`}
                    onClick={() => openCell('length', idx)}
                  >
                    {val || '–'}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ))}

      {/* Totals + validation */}
      <div className="cw-pars-summary">
        <span>Front {sumPars(tee.hole_pars, 0, 9)}</span>
        <span>Back {sumPars(tee.hole_pars, 9, 18)}</span>
        <span className="cw-pars-total">Par {parTotal}{parsComplete ? '' : ' (incomplete)'}</span>
        {anyLength && (
          <span className="cw-pars-length">{lengthTotal.toLocaleString()} {unitLabel}</span>
        )}
      </div>

      {!siComplete && (tee.hole_stroke_indices.some(v => v !== '' && v !== null)) && (
        <div className="cw-si-warning">
          <span>
            {dupHoles.size > 0 ? 'Some stroke indexes are used more than once. ' : ''}
            {missing.length > 0 ? `Missing: ${missing.join(', ')}.` : ''}
          </span>
          <button className="cw-si-fix" onClick={autoFixSI}>Auto-fix</button>
        </div>
      )}
      {siComplete && tee.hole_stroke_indices.every(v => v !== '' && v !== null) && (
        <div className="cw-si-ok">✓ All 18 stroke indexes used once</div>
      )}

      {/* Editors */}
      {editingCell && (
        <CellPicker
          type={editingCell.field}
          holeNumber={editingCell.holeIdx + 1}
          value={tee[CELL_FIELD[editingCell.field]]?.[editingCell.holeIdx]}
          unit={unitLabel}
          strokeIndices={tee.hole_stroke_indices}
          currentHoleIdx={editingCell.holeIdx}
          onSelect={handleCellSelect}
          onClear={handleCellClear}
          onClose={() => setEditingCell(null)}
        />
      )}

      {showKeypad && (
        <HoleEntryKeypad
          holePars={tee.hole_pars}
          holeStrokeIndices={tee.hole_stroke_indices}
          onUpdate={(field, holeIdx, value) => updateTeeHole(tee._key, field, holeIdx, value)}
          onClose={() => setShowKeypad(false)}
          allowDuplicateSI
        />
      )}
    </div>
  );
};

export default StepPars;
